<script context="module" lang="ts">
	export type Role =
		| ('collaborator' | 'owner' | 'moderator' | 'member')
		// eslint-disable-next-line @typescript-eslint/ban-types
		| (string & {})
		| undefined;
	export type RoleOption = 'collaborator' | 'owner' | 'moderator' | 'member' | 'remove';
</script>

<script lang="ts">
	import Popover from '$lib/components/Popover/Popover.svelte';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import { _ } from 'svelte-i18n';

	export let selectedRole: Role | undefined = 'collaborator';
	export let disableDropdown = false;
	export let onChange: (role: Role) => void = () => {
		// Nothing
	};
	export let onRemoveUser: () => void = () => {
		// Nothing
	};

	let isOpen: boolean;

	const setRole = (role: Role) => {
		selectedRole = role;
		onChange(role);
		isOpen = false;
	};

	export let renderedOptions: Array<RoleOption> = ['collaborator', 'owner', 'moderator', 'remove'];
	export let disabledOptions: Array<RoleOption> = [];
</script>

<Popover bind:isOpen position="bottom-inline-start" disabled={disableDropdown} maxHeight="325px">
	<div slot="target">
		{$_('common.role')}: {selectedRole === 'member'
			? $_(`common.${selectedRole}`)
			: $_(`common.roles.${selectedRole}`)}
		{#if !disableDropdown}
			<Icon icon="dropdown-arrow" size="20" />
		{/if}
	</div>
	<div class="wrapper" slot="content">
		<div class="header">
			{$_('hubs.group.change.role')}
		</div>
		<div class="menu">
			{#if renderedOptions.includes('owner')}
				<button
					type="button"
					on:click={() => setRole('owner')}
					disabled={disabledOptions.includes('owner')}>
					<h4 class:selected={selectedRole === 'owner'}>{$_('common.roles.owner')}</h4>
					<ul>
						<li>{$_('hubs.group.delete.hub')}</li>
						<li>{$_('hubs.group.complete.actions.below')}</li>
					</ul>
				</button>
			{/if}
			{#if renderedOptions.includes('moderator')}
				<button
					type="button"
					on:click={() => setRole('moderator')}
					disabled={disabledOptions.includes('moderator')}>
					<h4 class:selected={selectedRole === 'moderator'}>{$_('common.roles.moderator')}</h4>
					<ul>
						<li>{$_('hubs.group.moderator.item.one')}</li>
						<li>{$_('hubs.group.moderator.item.two')}</li>
						<li>{$_('hubs.group.moderator.item.three')}</li>
						<li>{$_('hubs.group.moderator.item.four')}</li>
					</ul>
				</button>
			{/if}
			{#if renderedOptions.includes('collaborator')}
				<button
					type="button"
					on:click={() => setRole('collaborator')}
					disabled={disabledOptions.includes('collaborator')}>
					<h4 class:selected={selectedRole === 'collaborator'}>
						{$_('common.roles.collaborator')}
					</h4>
					<ul>
						<li>{$_('hubs.group.collaborator.item.one')}</li>
						<li>{$_('hubs.group.collaborator.item.two')}</li>
						<!-- <li>{$_('hubs.group.collaborator.item.three')}</li> -->
					</ul>
				</button>
			{/if}
			{#if renderedOptions.includes('member')}
				<button
					disabled={disabledOptions.includes('member')}
					type="button"
					on:click={() => setRole('member')}>
					<h4 class:selected={selectedRole === 'member'}>{$_('common.member')}</h4>
					<ul>
						<li>{$_('hubs.group.member.item.one')}</li>
						<li>{$_('hubs.group.member.item.two')}</li>
					</ul>
				</button>
			{/if}
			{#if renderedOptions.includes('remove')}
				<button
					type="button"
					disabled={disabledOptions.includes('remove')}
					on:click={() => {
						onRemoveUser();
						isOpen = false;
					}}>
					<h4 class="remove">{$_('common.remove')}</h4>
					<ul>
						<li>{$_('hubs.group.remove.community')}</li>
					</ul>
				</button>
			{/if}
		</div>
	</div>
</Popover>

<style>
	ul {
		margin-block: 0 0;
		margin-inline-start: var(--gutterMedium);
		padding-inline-start: var(--gutterSmall);
		list-style: disc;
	}

	button {
		inline-size: 100%;
		padding: 0;
		border-width: 0;
		background-color: transparent;
		font-family: inherit;
		font-size: inherit;
		font-style: inherit;
		font-weight: inherit;
		line-height: inherit;
	}

	.wrapper {
		color: var(--text-color);
	}

	.menu > button {
		padding: var(--gutterSmall);
		border-block-start: 1px solid var(--grayLight);
		cursor: pointer;
	}

	.header {
		padding: var(--gutterSmall);
		padding-inline-start: var(--gutterMedium);
		user-select: none;
	}

	.menu > button:last-child {
		padding-block-end: var(--gutterMedium);
	}

	.menu > button:hover {
		background-color: var(--grayLighter);
	}

	li {
		margin: 0;
		padding-block: 2px;
		padding-inline: 0;
		font-size: var(--fontSizeSmall);
		text-align: start;
		white-space: nowrap;
	}

	h4 {
		display: flex;
		justify-content: space-between;
		margin-block: 0 var(--gutterSmallest);
		padding-inline: var(--gutterSmall);
		color: rgb(76 83 93);
		/* bold */
		font-weight: 600;
	}

	.selected {
		color: var(--lightenedPrimaryAccentColor);
	}

	.remove {
		color: rgb(254 106 102);
	}

	.selected::after {
		content: '✓';
	}
</style>
